import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Container } from 'components/elements';
import { ChatProvider, useChat } from './context/ChatContext';
import Sidebar from './Sidebar';
import InnerChatView from './ChatView';
import {
  findConversationByThreadQuery,
  getMessageThreadPath,
  parseMessageThreadQuery,
} from './messageUrl';
import {
  ChatLayoutGrid,
  ChatMain,
  ChatPanelEmptyState,
  ChatViewScope,
  PageHeader,
  PageShell,
  PageTitle,
} from './chat.styled';

function ChatLayout() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const history = useHistory();
  const location = useLocation();
  const { activeConversation, setActiveConversation, conversations, handleConversationSelect } = useChat();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 800);

  /** Legacy /messages/:id → /message?userid=…&group=… */
  React.useEffect(() => {
    if (conversationId === undefined || conversationId === '') return;
    const id = Number(conversationId);
    if (!Number.isFinite(id) || id <= 0) {
      history.replace('/messages');
      return;
    }
    const found = conversations.find((c) => c.id === id);
    if (conversations.length > 0) {
      if (!found) {
        history.replace('/messages');
        return;
      }
      history.replace(getMessageThreadPath(found));
    }
  }, [conversationId, conversations, history]);

  /** /message?userid=&group= */
  React.useEffect(() => {
    if (location.pathname !== '/message') return;
    const parsed = parseMessageThreadQuery(location.search);
    if (!parsed) {
      history.replace('/messages');
      return;
    }
    const { userid, group } = parsed;
    const found = findConversationByThreadQuery(conversations, userid, group);
    if (conversations.length > 0) {
      if (!found) {
        history.replace('/messages');
        return;
      }
      if (activeConversation?.id !== found.id) {
        handleConversationSelect(found);
      }
    }
  }, [
    location.pathname,
    location.search,
    conversations,
    history,
    handleConversationSelect,
    activeConversation,
  ]);

  /** Inbox: /messages with no thread */
  React.useEffect(() => {
    if (location.pathname !== '/messages') return;
    if (conversationId) return;
    setActiveConversation((prev) => (prev !== null ? null : prev));
  }, [location.pathname, conversationId, setActiveConversation]);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const showSidebar = !isMobile || !activeConversation;
  const showChat = !isMobile || !!activeConversation;

  return (
    <Container>
      <PageShell>
        <PageHeader>
          <PageTitle>Messages</PageTitle>
        </PageHeader>
        <ChatViewScope>
          <ChatLayoutGrid>
            <Sidebar visibleOnMobile={showSidebar} />
            <ChatMain $visibleOnMobile={showChat}>
              {activeConversation ? (
                <InnerChatView onBack={isMobile ? () => history.push('/messages') : undefined} />
              ) : (
                <ChatPanelEmptyState>Select or start a conversation.</ChatPanelEmptyState>
              )}
            </ChatMain>
          </ChatLayoutGrid>
        </ChatViewScope>
      </PageShell>
    </Container>
  );
}

const ChatViewPage: React.FC = () => (
  <ChatProvider>
    <ChatLayout />
  </ChatProvider>
);

export default ChatViewPage;
